import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';

/**
 * Formly field type that renders a native `<select>` element.
 *
 * Props:
 * - `options` – `{ value: any; label: string }[]`  The list of options to display.
 * - `label`   – `string`  Standard Formly label (used as aria-label when no visible label is present).
 *
 * The component preserves the original type of each option's `value` (number, string, etc.)
 * rather than coercing everything to string as a raw `<select>` would.
 *
 * Usage:
 * ```typescript
 * {
 *   key: 'fontFamily',
 *   type: 'native-select',
 *   props: {
 *     label: 'Font Family',
 *     options: [
 *       { value: 'helvetica', label: 'Helvetica / Arial' },
 *       { value: 'times', label: 'Times New Roman' },
 *     ],
 *   },
 * }
 * ```
 */
@Component({
  selector: 'app-native-select-formly-type',
  standalone: true,
  imports: [],
  templateUrl: './native-select-formly-type.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './native-select-formly-type.component.css',
})
export class NativeSelectFormlyTypeComponent extends FieldType<FieldTypeConfig> {
  get showLabel(): boolean {
    return this.props['showLabel'] === true;
  }

  get selectLabel(): string {
    return String(this.props['label'] ?? '');
  }

  get selectOptions(): { value: any; label: string }[] {
    return (this.props['options'] as { value: any; label: string }[]) ?? [];
  }

  onChange(event: Event): void {
    const rawValue = (event.target as HTMLSelectElement).value;
    // Preserve original type — try to find a matching option first
    const matched = this.selectOptions.find((o) => String(o.value) === rawValue);
    const value = matched !== undefined ? matched.value : rawValue;
    this.formControl.setValue(value);
    this.formControl.markAsDirty();
  }
}
